import { useEffect, useRef } from 'react';
import { isTrustedShellOrigin } from '../constants/origins';

interface SSEEventHandlers {
  enabled: boolean;
  onConversationNew?: (data: any) => void;
  onMessageReceived?: (data: any) => void;
  onMessageSent?: (data: any) => void;
  onScenarioTriggered?: (data: any) => void;
  onPlatformStatus?: (data: any) => void;
}

export const useSSE = ({
  enabled,
  onConversationNew,
  onMessageReceived,
  onMessageSent,
  onScenarioTriggered,
  onPlatformStatus,
}: SSEEventHandlers) => {
  const eventSourceRef = useRef<EventSource | null>(null);

  // Keep handlers up-to-date without causing effect re-runs.
  const handlersRef = useRef({
    onConversationNew,
    onMessageReceived,
    onMessageSent,
    onScenarioTriggered,
    onPlatformStatus,
  });

  useEffect(() => {
    handlersRef.current = {
      onConversationNew,
      onMessageReceived,
      onMessageSent,
      onScenarioTriggered,
      onPlatformStatus,
    };
  }, [onConversationNew, onMessageReceived, onMessageSent, onScenarioTriggered, onPlatformStatus]);

  useEffect(() => {
    if (!enabled) return;

    // ── Iframe mode: receive SSE events relayed from biztada-app shell ────────
    // When this app runs inside the shell's iframe the shell owns the EventSource
    // and forwards events via postMessage.  Direct SSE connection is skipped to
    // avoid N duplicate connections to the same backend.
    if (window !== window.parent) {
      const processEvent = (sseEvent: { type: string; payload: any }) => {
        switch (sseEvent.type) {
          case 'conversation.new':
            console.log('📡 SSE: conversation.new (shell relay)', sseEvent.payload);
            handlersRef.current.onConversationNew?.(sseEvent.payload);
            break;
          case 'message.received':
            console.log('📡 SSE: message.received (shell relay)', sseEvent.payload);
            handlersRef.current.onMessageReceived?.(sseEvent.payload);
            break;
          case 'message.sent':
            console.log('📡 SSE: message.sent (shell relay)', sseEvent.payload);
            handlersRef.current.onMessageSent?.(sseEvent.payload);
            break;
          case 'scenario.triggered':
            console.log('📡 SSE: scenario.triggered (shell relay)', sseEvent.payload);
            handlersRef.current.onScenarioTriggered?.(sseEvent.payload);
            break;
          case 'platform.status':
            console.log('📡 SSE: platform.status (shell relay)', sseEvent.payload);
            handlersRef.current.onPlatformStatus?.(sseEvent.payload);
            break;
        }
      };

      const handleShellMessage = (event: MessageEvent) => {
        if (!isTrustedShellOrigin(event.origin)) return;
        if (event.data?.source !== 'biztada-shell') return;

        if (event.data.type === 'SSE_EVENT') {
          processEvent(event.data.event);
        } else if (event.data.type === 'SSE_CATCHUP') {
          (event.data.events as { type: string; payload: any }[]).forEach(processEvent);
        }
      };

      window.addEventListener('message', handleShellMessage);
      return () => window.removeEventListener('message', handleShellMessage);
    }

    // ── Standalone mode: direct EventSource connection (unchanged behaviour) ──
    const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
    const token = localStorage.getItem('session_token');

    if (!token) {
      console.warn('⚠️ No session token - SSE connection skipped');
      return;
    }

    const url = `${apiUrl}/events/subscribe?token=${encodeURIComponent(token)}`;
    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    console.log('📡 SSE: Connecting to', url);

    eventSource.onopen = () => {
      console.log('✅ SSE: Connected');
    };

    eventSource.addEventListener('conversation.new', (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      console.log('📡 SSE: conversation.new', data);
      handlersRef.current.onConversationNew?.(data);
    });

    eventSource.addEventListener('message.received', (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      console.log('📡 SSE: message.received', data);
      handlersRef.current.onMessageReceived?.(data);
    });

    eventSource.addEventListener('message.sent', (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      console.log('📡 SSE: message.sent', data);
      handlersRef.current.onMessageSent?.(data);
    });

    eventSource.addEventListener('scenario.triggered', (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      console.log('📡 SSE: scenario.triggered', data);
      handlersRef.current.onScenarioTriggered?.(data);
    });

    eventSource.addEventListener('platform.status', (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      console.log('📡 SSE: platform.status', data);
      handlersRef.current.onPlatformStatus?.(data);
    });

    eventSource.onerror = (error) => {
      console.error('❌ SSE: Connection error', error);
      // EventSource will automatically reconnect
    };

    return () => {
      console.log('📡 SSE: Disconnecting');
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [enabled]);

  return eventSourceRef.current;
};
